/**
 * Normalized reasoning/thinking control → provider-specific request fragment.
 *
 * Pure & dependency-free (no `ai.libx.js` import — provider is detected from the
 * `provider/model` prefix), so it stays inside the Agent's structural-typing boundary.
 * The returned fragment is spread into `ai.chat()`; callers never hand-craft the
 * per-provider encoding (Anthropic `thinking.budget_tokens`, OpenAI `reasoning_effort`).
 */

/** `'off'`/undefined = no reasoning; named buckets; or a raw token budget (budget-based providers). */
export type ReasoningEffort = 'off' | 'low' | 'medium' | 'high' | number;

export interface ChatFragment {
  providerOptions?: Record<string, unknown>;
  /** Request `max_tokens`. Raised for budget-based providers so `budget_tokens < max_tokens` holds. */
  maxTokens?: number;
}

/** Named effort → thinking-token budget (budget-based providers, e.g. Anthropic). */
const BUDGET: Record<'low' | 'medium' | 'high', number> = { low: 2048, medium: 8192, high: 24576 };

/** Bucket a raw token budget back to the nearest named effort (for providers that take a label). */
function toLabel(effort: ReasoningEffort): 'low' | 'medium' | 'high' {
  if (typeof effort !== 'number') return effort === 'off' ? 'low' : effort;
  return effort <= BUDGET.low ? 'low' : effort < BUDGET.high ? 'medium' : 'high';
}

/** Resolve an effort to a concrete token budget. */
function toBudget(effort: ReasoningEffort): number {
  return typeof effort === 'number' ? effort : BUDGET[effort as 'low' | 'medium' | 'high'];
}

/**
 * Map a normalized effort to a partial `ChatOptions` fragment for `model`'s provider.
 * Returns `{}` (safe no-op) for `'off'`, undefined, or an unsupported provider.
 *
 * Supported: `anthropic/*` (extended thinking), `openai/*` (reasoning_effort).
 * `google/*` is intentionally NOT mapped here — its adapter `Object.assign`s
 * providerOptions over the built `generationConfig`, clobbering it; pass an
 * explicit `providerOptions` if you need Gemini thinking.
 */
export function reasoningToChatFragment(model: string, effort?: ReasoningEffort): ChatFragment {
  if (effort == null || effort === 'off') return {};
  const provider = model.split('/')[0];
  switch (provider) {
    case 'anthropic': {
      const budget = toBudget(effort);
      return { providerOptions: { thinking: { type: 'enabled', budget_tokens: budget } }, maxTokens: budget + 8192 };
    }
    case 'openai':
      return { providerOptions: { reasoning_effort: toLabel(effort) } };
    default:
      return {};
  }
}
